import clsx from "clsx";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { toAbsoluteUrl } from "../../../../../../resources/helpers";
import { RootState } from "../../../../../../setup/redux/store";
import { KeyValues } from "../../../../../../util/Formatter";
import { convertTextStyle } from "../../../../../../util/TextFormatter";
import { excerpt } from "../../../../../../util/excerpt";
import TemplateLanguage from "../../../../../models/Language";
import { InteractiveButton } from "../../../../../modules/template/redux/TemplateRedux";

interface Props {
  template?: TemplateLanguage;
  previewHeaderText?: any;
  previewBody?: any;
  chatHeader?: {
    background: string;
    color: string;
    avatar?: string;
    accountName: string;
  };
}

interface ButtonCTAProps {
  usedFor: string;
  title: string;
}

const ButtonCTA: React.FC<ButtonCTAProps> = ({ usedFor, title }) => {
  return (
    <div
      role="button"
      className="d-flex text-center mt-1 search-toolbar text-center p-3 cl-chat-whatsapp w-full"
      style={{
        alignContent: "center",
        justifyContent: "center",
      }}
    >
      <div className="d-flex" style={{ alignItems: "center" }}>
        {usedFor === "PHONE_NUMBER" && (
          <i
            data-testid="preview-cta-pn"
            className="bi bi-telephone-fill mr-4"
            style={{ color: "#00a5f4" }}
          ></i>
        )}
        {usedFor === "URL" && (
          <i
            data-testid="preview-cta-url"
            className="fas fa-external-link-alt mr-4"
            style={{ color: "#00a5f4" }}
          ></i>
        )}
        {usedFor === "COPY_CODE" && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="#00a5f4"
            className="bi bi-copy mr-4"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
            />
          </svg>
        )}
        {usedFor === "SEE_ALL" && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 56 56"
          >
            <path
              fill="#00a5f4"
              fill-rule="evenodd"
              d="M10 36a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2c0 1.105-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 39c0-1.105.89-2 2.002-2zM10 26a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2c0 1.105-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 29c0-1.105.89-2 2.002-2zM10 16a3 3 0 1 1 0 6a3 3 0 0 1 0-6m35.998 1c1.106 0 2.002.888 2.002 2c0 1.105-.89 2-2.002 2H18.002A1.996 1.996 0 0 1 16 19c0-1.105.89-2 2.002-2z"
            />
          </svg>
        )}
        <strong
          data-testid="preview-cta"
          style={{ marginLeft: "10px", color: "#00a5f4" }}
        >
          {title}
        </strong>
      </div>
    </div>
  );
};

const ButtonQuickReply: React.FC<{
  reduxQuickReply: string[];
  className?: string;
}> = ({ reduxQuickReply, className }) => {
  const isAllFull = getIsAllFull(reduxQuickReply);
  const firstFullElementIndex = reduxQuickReply.findIndex(
    (text) => text.length > 10
  );

  return (
    <div
      className={`mt-1 ${className || ""}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "4px",
      }}
    >
      {reduxQuickReply.map((text, i) => {
        let isFull = text.length > 10;
        if (
          reduxQuickReply.length > 2 &&
          i === reduxQuickReply.length - 1 &&
          firstFullElementIndex !== 0
        ) {
          isFull = true;
        }
        if (text) {
          return (
            <div
              key={i}
              role="button"
              className="d-flex align-items-center justify-content-center search-toolbar text-center p-2 cl-chat-whatsapp w-full"
              style={{
                gridColumn:
                  isFull || isAllFull ? "span 2 / span 2" : "span 1 / span 1",
              }}
            >
              <span
                data-testid="preview-cta-qr"
                className="px-2"
                style={{ color: "#00a5f4" }}
              >
                {text}
              </span>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const MessagePreview: React.FC<Props & React.HTMLAttributes<HTMLElement>> = ({
  // previewBody: campaignPreviewBody,
  previewHeaderText,
  template,
  className,
  chatHeader,
}) => {
  // Redux
  const reduxPreviewHeaderText = useSelector(
    (state: RootState) => state?.Template.templatePreviewHeaderText
  );
  const reduxHeaderFile = useSelector(
    (state: RootState) => state?.Template.templateHeaderFile
  );
  const reduxHeaderURL = useSelector(
    (state: RootState) => state?.Template.templateHeaderURL
  );
  const reduxFileType = useSelector(
    (state: RootState) => state?.Template.templateFileType
  );
  const reduxFooter = useSelector(
    (state: RootState) => state?.Template.templateFooter
  );
  const reduxButtonCta = useSelector(
    (state: RootState) => state?.Template.templateButton
  );
  const reduxstate = useSelector(
    (state: RootState) => state
  );
  const reduxQuickReply = useSelector(
    (state: RootState) => state?.Template.templateQuickReplies
  );
  const previewBody = useSelector(
    (state: RootState) => state?.Template.templatePreviewBody
  );
  const selectedButton = useSelector(
    (state: RootState) => state?.Template.selectedInteractiveButton
  );

  //check isTabletOrMobile
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });

  // State
  const [readMore, setReadMore] = useState(false);
  const [displayedBody, setDisplayedBody] = useState("");

  // 768 is default text lenght determined by whatsapp to show ...Read more
  const READMORE_LEN = 768;

  // Use Effect for Re-assign Body Message Value & track Read More status
  useEffect(() => {
    const previewBodyString = //campaignPreviewBody ? campaignPreviewBody :
      previewBody;
    if (previewBodyString.length > READMORE_LEN) {
      const body = excerpt(previewBodyString, READMORE_LEN);
      setDisplayedBody(body);
    } else {
      setDisplayedBody(previewBodyString);
    }
    setReadMore(previewBodyString.length > READMORE_LEN);
  }, [previewBody]); //, campaignPreviewBody]);

  const ReadMore = () => {
    const showReadMore = () => {
      setReadMore(false);
      // if (campaignPreviewBody) {
      //   setDisplayedBody(campaignPreviewBody);
      // } else {
      setDisplayedBody(previewBody);
      // }
    };

    if (!readMore) return <></>;

    return (
      <div
        role="button"
        onClick={showReadMore}
        style={{
          color: "#00a5f4",
          textDecoration: "underline",
          display: "inline",
          cursor: "pointer",
        }}
      >
        {t("Common.ReadMore")}
      </div>
    );
  };

  let combinedButtons = [
    ...(template?.buttons?.map((cta: any) => ({ ...cta, source: "cta" })) || []),
  ];

  const renderButtons = () => {
    let combinedButtons = [
      ...(template?.buttons?.map((cta: any) => ({ ...cta, source: "cta" })) || []),
    ];

    let displayedButtons = combinedButtons?.slice(0, 2);

    console.log(displayedButtons, "disbut combine stp");
    console.log(combinedButtons, "check combine stp");

    return (
      <>
        {displayedButtons?.map((button, i) => (
          <React.Fragment key={i.toString()}>
            {button.source === "cta" && button.type === "PHONE_NUMBER" && (
              <ButtonCTA
                key={`cta-phone-${i}`}
                usedFor="PHONE_NUMBER"
                title={button.text}
              />
            )}
            {button.source === "cta" && button.type === "URL" && (
              <ButtonCTA
                key={`cta-url-${i}`}
                usedFor="URL"
                title={button.text}
              />
            )}
            {button.source === "cta" && button.type === "OTP" && (
              <ButtonCTA
                key={`cta-otp-${i}`}
                usedFor="COPY_CODE"
                title={button.text}
              />
            )}
            {button.source === "cta" && button.type === "QUICK_REPLY" && (
              <ButtonQuickReply
                key={`quickreply-${i}`}
                reduxQuickReply={[button.text]}
              />
            )}
          </React.Fragment>
        ))}
        {combinedButtons.length > 2 && (
          <ButtonCTA
            key="see-all-options"
            usedFor="SEE_ALL"
            title="See All Options"
          />
        )}
      </>
    );
  };

  return (
    <div className="w-100">
      <div className="mb-10 h-100">
        {chatHeader ? (
          <div
            style={{
              width: "100%",
              height: "3.5rem",
              backgroundColor: chatHeader.background,
              color: chatHeader.color,
            }}
            className="d-flex align-items-center justify-content-between"
            data-testid="header-whatsapp-chat"
          >
            <div className="d-flex align-items-center gap-1">
              <i
                style={{ color: chatHeader.color, fontSize: "2rem" }}
                className="bi bi-arrow-left-short"
              ></i>
              <div className="symbol symbol-30px me-1">
                <img
                  id="active_user_avatar"
                  style={{ borderRadius: "50%" }}
                  data-testid="CustomHeaderClientAvatarImage"
                  src={toAbsoluteUrl(
                    chatHeader?.avatar || "/media/icons/avatar/client-icon.png"
                  )}
                  alt="avatar"
                />
              </div>
              <span style={{ fontSize: "1rem", fontWeight: 600 }}>
                {chatHeader.accountName}
              </span>
            </div>

            <div className="d-flex align-items-center gap-1">
              <i
                style={{ color: chatHeader.color, fontSize: "1.25rem" }}
                className="bi bi-camera-video-fill me-3"
              ></i>
              <i
                style={{ color: chatHeader.color, fontSize: "1.25rem" }}
                className="bi bi-telephone-fill me-2"
              ></i>
              <i
                style={{ color: chatHeader.color, fontSize: "1.25rem" }}
                className="bi bi-three-dots-vertical me-2"
              ></i>
            </div>
          </div>
        ) : null}
        <div
          className="h-100 p-5"
          style={{
            backgroundImage: `url(${toAbsoluteUrl(
              "/media/misc/wa-bg-review.png"
            )})`,
            maxWidth: "600px",
            minHeight: isTabletOrMobile ? "300px" : "",
          }}
          data-testid="preview-header-document"
        >
          {!template ? (
            <div>
              <div
                data-testid="preview-message"
                className={clsx(
                  "w-75",
                  "d-flex flex-column flex-column-fluid cl-chat-whatsapp p-2 search-toolbar"
                )}
              >
                {(reduxHeaderURL !== "" || !reduxHeaderFile) &&
                  reduxFileType === "image" && (
                    <div
                      className={clsx("mw-100 search-toolbar", {
                        "wrapper mb-3": reduxHeaderURL !== "",
                        "display-none": !reduxFileType,
                      })}
                      style={{
                        backgroundImage: `url(${toAbsoluteUrl(
                          reduxHeaderURL
                          // "/media/illustrations/sketchy-1/AssetPwEntry.png"
                        )})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        paddingLeft: 100,
                      }}
                      data-testid="preview-header-image"
                    ></div>
                  )}

                {(reduxHeaderURL !== "" || !reduxHeaderFile) &&
                  reduxFileType === "video" && (
                    <video
                      className={clsx("mb-3", {
                        "display-none": !(reduxHeaderURL !== ""),
                      })}
                      // className="w-300px"
                      src={reduxHeaderURL}
                      style={{ borderRadius: "8px" }}
                      data-testid="preview-header-video"
                    ></video>
                  )}

                {reduxHeaderFile && (
                  <div
                    className={clsx(
                      "wrapper search-toolbar mb-3 bi bi-file-earmark fs-5x cl-graysilver",
                      { "display-none": !(reduxHeaderURL !== "") }
                    )}
                    style={{
                      paddingTop: "0%",
                      paddingLeft: "40%",
                    }}
                    data-testid="preview-header-document"
                  ></div>
                )}

                {/* Header Preview */}
                {reduxPreviewHeaderText !== "" && (
                  <div
                    className={clsx("textBold")}
                    style={{ fontWeight: "bold" }}
                    dangerouslySetInnerHTML={{
                      __html: reduxPreviewHeaderText,
                    }}
                  ></div>
                )}

                {/* Body Preview */}
                <div
                  data-testid="preview-body"
                  className={
                    clsx()
                    // hasTemplate ? "" : "display-none"
                  }
                  dangerouslySetInnerHTML={{
                    __html: displayedBody,
                  }}
                ></div>
                <ReadMore />

                {/* Footer Preview */}
                {reduxFooter !== "" && (
                  <div
                    data-testid="preview-footer"
                    className={clsx("mt-3", "textBold")}
                    style={{ color: "gray" }}
                    dangerouslySetInnerHTML={{
                      __html: reduxFooter,
                    }}
                  ></div>
                )}
              </div>

              {/* Show Cta Button
              {selectedButton === InteractiveButton.CallToAction &&
              reduxButtonCta !== undefined &&
              reduxButtonCta.length > 0
                ? reduxButtonCta.map((cta, i) => (
                    <React.Fragment key={i.toString()}>
                      {cta.type === "PHONE_NUMBER" && (
                        <ButtonCTA usedFor="PHONE_NUMBER" title={cta.text} />
                      )}
                      {cta.type === "URL" && (
                        <ButtonCTA usedFor="URL" title={cta.text} />
                      )}
                    </React.Fragment>
                  ))
                : ""}

              {selectedButton === InteractiveButton.QuickReply &&
              reduxQuickReply.length ? (
                <ButtonQuickReply
                  reduxQuickReply={reduxQuickReply}
                  className="w-75"
                />
              ) : (
                ""
              )} */}

              {/* {selectedButton === InteractiveButton.CallToAction &&
                renderButtons()}
              {(selectedButton === InteractiveButton.QuickReply ||
                selectedButton === InteractiveButton.OTP) &&
                renderButtons()} */}
              {combinedButtons.length > 0 && renderButtons()}
            </div>
          ) : null}

          {template && (
            <div>
              <div
                className={clsx(
                  "d-flex flex-column cl-chat-whatsapp m-1 search-toolbar mw-80"
                )}
              >
                {/* Preview Header */}
                {/* Header Text */}
                {previewHeaderText && previewHeaderText !== "" && (
                  <div
                    className="m-3 step-sub-title"
                    dangerouslySetInnerHTML={{
                      __html: previewHeaderText,
                    }}
                  ></div>
                  // >{headerText}</div>
                )}

                {!previewHeaderText &&
                  template.header?.type === "text" &&
                  template.header?.text && (
                    <div
                      className={clsx(
                        "m-3 fw-bolder",
                        template?.id && template.id !== "" ? "" : "display-none"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: convertTextStyle(
                          replacePreviewHeader(template)
                        ),
                      }}
                    ></div>
                  )}

                {/* Header Document, Video, Image */}
                {/* <div>${JSON.stringify(headerAttachObj)}</div> */}
                {template !== undefined &&
                  template.header?.type === "media" &&
                  template.header.mediaType === "document" && (
                    <div className="d-flex flex-fill bg-secondary m-3 search-toolbar mw-80 h-auto align-items-start image-input">
                      <div className="w-30 m-2 align-self-center">
                        <i className="bi bi-file-earmark-text fs-3x text-dark"></i>
                      </div>
                      <div className="flex-grow-1 m-2 align-self-center">
                        {template.header.fileName ||
                          template.header.filename ||
                          ""}
                      </div>
                      <div className="w-30 m-5 align-self-center">
                        <i className="bi bi-download fs-1 text-dark"></i>
                      </div>
                    </div>
                  )}

                {template &&
                  template.header?.type === "media" &&
                  template.header.mediaType === "image" &&
                  template.header.headerURL !== undefined && (
                    <div
                      className="wrapper mw-100 search-toolbar m-3"
                      style={{
                        backgroundImage: `url(${template.header.headerURL})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                      data-testid="preview-header-image"
                    ></div>
                  )}
                {template &&
                  template.header?.type === "media" &&
                  template.header.mediaType === "video" &&
                  template.header.headerURL !== undefined && (
                    <div className="p-3">
                      <video
                        controls
                        className={clsx("mw-100")}
                        src={template.header.headerURL}
                        style={{ borderRadius: "8px" }}
                        data-testid="preview-header-video"
                      ></video>
                    </div>
                  )}
                {template &&
                  template.header?.type === "media" &&
                  template.header.mediaType !== "document" &&
                  template.header.headerURL === undefined && (
                    <div className="d-flex flex-column bg-secondary m-3 search-toolbar mw-80 h-auto align-items-center image-input">
                      <i
                        className={clsx(
                          "fs-5x text-dark",
                          template.header.mediaType === "image"
                            ? "bi bi-card-image"
                            : "bi-play-btn-fill"
                        )}
                      ></i>
                    </div>
                  )}

                {/* Preview Body Text Message */}
                <div
                  className={clsx(
                    "m-3",
                    template?.id && template.id !== "" ? "" : "display-none"
                  )}
                  dangerouslySetInnerHTML={{
                    // __html: replacePreviewBody(previewBody),
                    __html: convertTextStyle(replacePreviewBody(template)),
                  }}
                ></div>
                {/* Preview Body Text Message */}

                {/* Footer Preview */}
                {template &&
                  template.id !== "" &&
                  template.footer &&
                  template.footer !== "" && (
                    <div
                      className={clsx("m-3", "textBold")}
                      style={{ color: "gray" }}
                      dangerouslySetInnerHTML={{
                        __html: template.footer,
                      }}
                    ></div>
                  )}
              </div>

              {/* {template.buttons &&
              template.buttons.length > 0 &&
              (template.buttons[0].type === "QUICK_REPLY" ||
                template.buttons[0].type === "OTP") ? (
                <ButtonQuickReply
                  reduxQuickReply={template.buttons.map((btn) => btn.text)}
                />
              ) : null}

              {template.buttons &&
              template.buttons.length > 0 &&
              (template.buttons[0].type === "QUICK_REPLY" ||
                template.buttons[0].type === "OTP")
                ? template.buttons.map((btn, i) => (
                    <React.Fragment key={i.toString()}>
                      {btn.type === "PHONE_NUMBER" && (
                        <ButtonCTA usedFor="PHONE_NUMBER" title={btn.text} />
                      )}
                      {btn.type === "URL" && (
                        <ButtonCTA usedFor="URL" title={btn.text} />
                      )}
                    </React.Fragment>
                  ))
                : null} */}
              {combinedButtons.length > 0 && renderButtons()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Preview Saved Template
const replacePreviewBody = (template: TemplateLanguage) => {
  let tempBody = "";

  if (template) {
    if (template.body) {
      tempBody = template.body.replaceAll("\n", "<br/>");
      // tempBody = template.body;
    }

    if (template.bodyParamCount) {
      template.bodyParam?.forEach((param, idx) => {
        const replaceIdx: string = `{{${idx + 1}}}`;

        const changedParam = { ...param };

        if (
          Object.keys(KeyValues).includes(changedParam.paramKey!) &&
          changedParam.paramKey !== `customText${idx + 1}`
        ) {
          tempBody = tempBody.replace(
            replaceIdx,
            `<span class="badge badge-primary fw-bolder fs-7 m-1"><i class="bi bi-server pe-2 text-white"></i>
              ${
                // changedParam.textContent && changedParam.textContent !== ""
                //   ? changedParam.textContent
                //   : t("Campaign.Input." + changedParam.paramKey)
                t("Campaign.Input." + changedParam.paramKey)
              }
            </span>`
          );
        } else {
          if (
            changedParam.paramKey === `customText${idx + 1}` &&
            (!changedParam.textContent || changedParam.textContent === "")
          ) {
            tempBody = tempBody.replace(
              replaceIdx,
              `<span class="badge badge-primary fw-bolder fs-7 m-1"><i class="bi bi-server pe-2 text-white"></i>${t(
                "Campaign.Input.CustomizeText"
              )}</span>`
            );
          } else {
            // console.log("Ini custom check 2 >> "+changedParam.textContent);
            const newLine = changedParam.textContent?.includes("\n")
              ? "<br/>"
              : "";
            const newTextContent = changedParam.textContent?.replaceAll(
              "\n",
              "<br/>"
            );
            tempBody = tempBody.replace(
              replaceIdx,
              `${newLine}<span class="badge badge-primary fw-bolder fs-7 m-1 text-wrap mw-100" style="text-align: start;"><i class="bi bi-server pe-2 text-white w-100"></i>${newTextContent}</span>`
            );
          }
        }
      });
    }
  }

  return tempBody;
};

function replacePreviewHeader(template: TemplateLanguage) {
  let result = "";

  if (template.header && template.header.type === "text") {
    if (template.header.text) {
      result = template.header.text.replaceAll("\n", "<br/>");
    }

    if (template.header.paramCount && template.header.headerParam) {
      template.header.headerParam.forEach((param, idx) => {
        const replaceIdx: string = `{{${idx + 1}}}`;

        const changedParam = { ...param };

        if (
          Object.keys(KeyValues).includes(changedParam.paramKey!) &&
          changedParam.paramKey !== `customText${idx + 1}`
        ) {
          result = result.replace(
            replaceIdx,
            `<span class="badge badge-primary fw-bolder fs-7 m-1"><i class="bi bi-server pe-2 text-white"></i>${t(
              "Campaign.Input." + changedParam.paramKey
            )}</span>`
          );
        } else {
          if (
            changedParam.paramKey === `customText${idx + 1}` &&
            (!changedParam.textContent || changedParam.textContent === "")
          ) {
            result = result.replace(
              replaceIdx,
              `<span class="badge badge-primary fw-bolder fs-7 m-1"><i class="bi bi-server pe-2 text-white"></i>${t(
                "Campaign.Input.CustomizeText"
              )}</span>`
            );
          } else {
            // console.log("Ini custom check 2 >> "+changedParam.textContent);
            const newLine = changedParam.textContent?.includes("\n")
              ? "<br/>"
              : "";
            const newTextContent = changedParam.textContent?.replaceAll(
              "\n",
              "<br/>"
            );
            result = result.replace(
              replaceIdx,
              `${newLine}<span class="badge badge-primary fw-bolder fs-7 m-1 text-wrap mw-100" style="text-align: start;"><i class="bi bi-server pe-2 text-white w-100"></i>${newTextContent}</span>`
            );
          }
        }
      });
    }
  }

  return result;
}

const getIsAllFull = (reduxQuickReply: string[]) => {
  let allFull = false;
  const lastIndex = reduxQuickReply.length - 1;
  if (reduxQuickReply.length < 2) {
    allFull = true;
  }
  if (reduxQuickReply.length <= 2) {
    if (
      reduxQuickReply[0].length > 10 ||
      reduxQuickReply[lastIndex].length > 10
    ) {
      allFull = true;
    }
  }

  if (
    reduxQuickReply[0].length > 10 &&
    reduxQuickReply[lastIndex].length > 10
  ) {
    allFull = true;
  }

  const firstFullElementIndex = reduxQuickReply.findIndex(
    (text) => text.length > 10
  );
  const allFullElement = reduxQuickReply.filter((text) => text.length > 10);
  if (
    firstFullElementIndex > 0 &&
    firstFullElementIndex !== 0 &&
    firstFullElementIndex !== lastIndex
  ) {
    allFull = true;
  }
  if (
    firstFullElementIndex === 0 &&
    reduxQuickReply[lastIndex].length <= 10 &&
    allFullElement.length > 1
  ) {
    allFull = true;
  }

  return allFull;
};

export default MessagePreview;
